import { Control } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';
import { ContentGroup } from '@rossum/components/content-group/ContentGroup';
import TextFieldControl from '../../../../../components/ReactHookForm/controls/TextFieldControl';
import FormTypeahead from '../../../../../components/ReactHookForm/FormTypeahead';
import { EXTRACTED_FIELD_TYPES_LINK } from '../../../../../constants/values';
import { boldText, linebreak, link } from '../../../../../lib/formaterValues';
import { FieldConfiguration } from '../../../../engines/components/FieldConfiguration';
import { useQueue } from '../../../../queues/hooks/useQueue';
import { useAutomationFlags } from '../../../automation/core/hooks/useAutomationFlags';
import { QueueSettingsParams } from '../../../routes/types';
import { FieldsFormValues } from '../../form-model/formModels';
import {
  isAiEngineAvailable,
  isFormerAiEngineAvailable,
  isThresholdAvailable,
} from './conditions';

type AiEngineSectionProps = {
  control: Control<FieldsFormValues> | undefined;
  formValues: FieldsFormValues;
  fieldId: string;
  engineUrl?: string;
};

export const AiEngineSection = ({
  control,
  formValues,
  fieldId,
  engineUrl,
}: AiEngineSectionProps) => {
  const intl = useIntl();
  // since its browser url, lets assume it's not fully reliable and require type guards
  const params = useParams<Partial<QueueSettingsParams>>();
  const queueId =
    params.queueId && !isNaN(Number(params.queueId))
      ? Number(params.queueId)
      : undefined;

  const { data: queue, isLoading, isError } = useQueue(queueId);

  const { queueLevelFlags } = useAutomationFlags();

  const showThresholdField =
    !isLoading && !isError && !queueLevelFlags.isUsingAssistant(queue);

  if (!engineUrl && isFormerAiEngineAvailable(formValues)) {
    return (
      <ContentGroup
        title={intl.formatMessage({
          id: 'features.queueSettings.fields.form.capturedAiEngine.title',
        })}
        description={intl.formatMessage(
          {
            id: 'features.queueSettings.fields.form.capturedAiEngine.description',
          },
          {
            boldText,
            linebreak,
            link: link(EXTRACTED_FIELD_TYPES_LINK),
          }
        )}
      >
        <FormTypeahead
          name="field.aiEngineFields"
          control={control}
          label={intl.formatMessage({
            id: 'features.queueSettings.fields.form.aiEngine.label',
          })}
          helperText={intl.formatMessage({
            id: 'features.queueSettings.fields.form.aiEngine.helperText',
          })}
          disablePortal
          options={[]}
          freeSolo
          multiple
          size="small"
        />

        {showThresholdField && isThresholdAvailable(formValues) && (
          <TextFieldControl
            ControllerProps={{ control, name: 'field.scoreThreshold' }}
            label={intl.formatMessage({
              id: 'features.queueSettings.fields.form.threshold.label',
            })}
            helperText={intl.formatMessage({
              id: 'features.queueSettings.fields.form.threshold.helperText',
            })}
            type="number"
            FieldLabelProps={{
              layout: 'floating',
            }}
          />
        )}
      </ContentGroup>
    );
  }

  return engineUrl && isAiEngineAvailable(formValues) ? (
    <FieldConfiguration engineFieldName={fieldId} engineUrl={engineUrl}>
      {showThresholdField && (
        <TextFieldControl
          ControllerProps={{ control, name: 'field.scoreThreshold' }}
          label={intl.formatMessage({
            id: 'features.queueSettings.fields.form.threshold.label',
          })}
          helperText={intl.formatMessage({
            id: 'features.queueSettings.fields.form.threshold.helperText',
          })}
          type="number"
          FieldLabelProps={{
            layout: 'floating',
          }}
        />
      )}
    </FieldConfiguration>
  ) : null;
};
